import './ConsiderationSettingsTable.scss';
import React from 'react';
import { FormGroup, Col, Row } from 'reactstrap';
import { Input } from '@availity/form';
import { ConsiderationSettingsRow } from '../../../../../slices/marketplace/pricing/models/consideration-settings.model';
import {
    ConsiderationTreatment,
    ConsiderationTreatments,
    getAllowedConsiderationTreatments,
    withLegacyTreatment,
} from '../../../../../slices/marketplace/pricing/models/consideration-treatment.enum';
import {
    TypeOfConsideration,
    TypesOfConsideration,
    isHeaderOnlyConsiderationType,
} from '../../../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { MarketplacePricing } from '../../../../../slices/marketplace/pricing/models/marketplace-pricing.model';
import {
    LevelOfService,
    LevelsOfService,
} from '../../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';

const GRID_TOTAL_COLUMNS = 12;

interface ConsiderationSettingsRowConfig {
    type: TypeOfConsideration;
    indented?: boolean;
}

const CONSIDERATION_SETTINGS_ROW_ORDER: ConsiderationSettingsRowConfig[] = [
    { type: TypeOfConsideration.CashAtClose },
    { type: TypeOfConsideration.DeferredFixedPayments },
    { type: TypeOfConsideration.DeferredVariablePayments },
    { type: TypeOfConsideration.Earnout, indented: true },
    { type: TypeOfConsideration.SyntheticJvEquity, indented: true },
    { type: TypeOfConsideration.JvEquity },
    { type: TypeOfConsideration.CorporateEquity },
    { type: TypeOfConsideration.CommonEquity, indented: true },
    { type: TypeOfConsideration.PreferredEquity, indented: true },
    { type: TypeOfConsideration.Other },
];

const getConsiderationTypeName = (type: TypeOfConsideration): string =>
    TypesOfConsideration.find((item) => item.id === type)?.name ?? '';

const TREATMENT_FIELD_BY_LEVEL_OF_SERVICE: Record<
    LevelOfService,
    keyof ConsiderationSettingsRow
> = {
    [LevelOfService.DiscountBrokerage]: 'discountBrokerageTreatment',
    [LevelOfService.FullServiceBrokerage]: 'fullServiceBrokerageTreatment',
    [LevelOfService.StrategicFinancialAdvisoryService]: 'sfasTreatment',
};

interface ConsiderationSettingsTableProps {
    considerationSettings: ConsiderationSettingsRow[];
    columns: LevelOfService[];
    onChange: (newState: ConsiderationSettingsRow[]) => void;
    setFormFieldValue: (name: string, value: string) => void;
    formState: MarketplacePricing;
    isReadonly?: boolean;
}

const ConsiderationSettingsTable = (props: ConsiderationSettingsTableProps) => {
    const SINGLE_COLUMN_FIELD_WIDTH_MD = 3;

    const isSingleColumn = props.columns.length === 1;
    const labelFieldWidthMd = isSingleColumn
        ? SINGLE_COLUMN_FIELD_WIDTH_MD
        : Math.floor(GRID_TOTAL_COLUMNS / (props.columns.length + 1));
    const columnFieldWidthMd = isSingleColumn
        ? SINGLE_COLUMN_FIELD_WIDTH_MD
        : labelFieldWidthMd;

    const getLevelOfServiceName = (levelOfService: LevelOfService) =>
        LevelsOfService.find((item) => item.id === levelOfService)?.name;

    const getTreatmentName = (treatment: ConsiderationTreatment) =>
        ConsiderationTreatments.find((item) => item.id === treatment)?.name;

    const treatmentSelect = (
        typeOfConsideration: TypeOfConsideration,
        levelOfService: LevelOfService
    ) => {
        const fieldKey = TREATMENT_FIELD_BY_LEVEL_OF_SERVICE[levelOfService];
        const formRowIndex = props.formState.considerationSettings.findIndex(
            (row) => row.id === typeOfConsideration
        );
        if (formRowIndex === -1) {
            return null;
        }
        const fieldName = `considerationSettings[${formRowIndex}].${fieldKey}`;
        const value = props.formState.considerationSettings[formRowIndex][
            fieldKey
        ] as ConsiderationTreatment;

        const treatmentOptions = withLegacyTreatment(
            getAllowedConsiderationTreatments(levelOfService),
            value
        );

        return (
            <Input
                type="select"
                name={fieldName}
                value={value}
                title={getTreatmentName(value)}
                onChange={(event) => {
                    const numVal = Number(
                        event.currentTarget.value
                    ) as ConsiderationTreatment;
                    const newState = props.considerationSettings.map((row) =>
                        row.id === typeOfConsideration
                            ? { ...row, [fieldKey]: numVal }
                            : row
                    );
                    props.setFormFieldValue(fieldName, numVal.toString());
                    props.onChange(newState);
                }}
                disabled={props.isReadonly}>
                {treatmentOptions.map((item) => (
                    <option
                        value={item.id}
                        key={fieldKey + item.id}
                        title={item.name}>
                        {item.name}
                    </option>
                ))}
            </Input>
        );
    };

    return (
        <div className="consideration-settings-table">
            <FormGroup>
                <Row className="center-items fee-item-row margin-bottom-1-rem consideration-settings-column-names d-none d-md-flex">
                    <Col md={labelFieldWidthMd}>
                        <label>Type of Consideration</label>
                    </Col>
                    {props.columns.map((levelOfService) => (
                        <Col md={columnFieldWidthMd} key={levelOfService}>
                            <label>
                                {getLevelOfServiceName(levelOfService)}
                            </label>
                        </Col>
                    ))}
                </Row>

                {CONSIDERATION_SETTINGS_ROW_ORDER.map(({ type, indented }) => {
                    // #255 — Deferred Variable Payments / Corporate Equity are header/label rows
                    // only; their treatment is configured on their indented children, so no
                    // inputs here.
                    const isHeaderOnly = isHeaderOnlyConsiderationType(type);
                    return (
                        <Row
                            key={type}
                            className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <label
                                    className={
                                        indented ? 'consideration-settings-label--indented' : undefined
                                    }>
                                    {getConsiderationTypeName(type)}
                                </label>
                            </Col>
                            {props.columns.map((levelOfService) => (
                                <Col
                                    md={columnFieldWidthMd}
                                    key={levelOfService}
                                    className="conditional-text-center multi-field flex-hor-center consideration-settings-cell">
                                    {!isHeaderOnly && (
                                        <>
                                            <label className="d-md-none level-of-service-label">
                                                {getLevelOfServiceName(levelOfService)}
                                            </label>
                                            {treatmentSelect(type, levelOfService)}
                                        </>
                                    )}
                                </Col>
                            ))}
                        </Row>
                    );
                })}
            </FormGroup>
        </div>
    );
};

export default ConsiderationSettingsTable;
